import {
  EFFECT_PRESET_SOURCE_GLOBAL,
  createEffectGlobalPresetExport,
} from '@/editor/effects/effectPresets'
import {
  TEXT_MOTION_PRESET_SOURCE_GLOBAL,
  createTextMotionGlobalPresetExport,
  normalizeTextMotionPreset,
} from '@/editor/text/textMotionPresets'
import {
  normalizeMoodCategory,
  normalizePersonalityCategories,
  normalizePersonalityCategory,
} from '@/etc/aiElements'
import { normalizeWorkflowPreset } from '@/etc/aiWorkflowPresets'
import {
  VYBY_PRESET_KIND_CAMERA_BODY,
  VYBY_PRESET_KIND_CAMERA_DEPTH_OF_FIELD,
  VYBY_PRESET_KIND_CAMERA_FOCAL,
  VYBY_PRESET_KIND_CAMERA_LENS,
  VYBY_PRESET_KIND_CAMERA_MOTION,
  VYBY_PRESET_KIND_CAMERA_TRANSITION_IN,
  VYBY_PRESET_KIND_CAMERA_TRANSITION_OUT,
} from '@/etc/cameraPresetKinds'

export {
  VYBY_PRESET_KIND_CAMERA_BODY,
  VYBY_PRESET_KIND_CAMERA_DEPTH_OF_FIELD,
  VYBY_PRESET_KIND_CAMERA_FOCAL,
  VYBY_PRESET_KIND_CAMERA_LENS,
  VYBY_PRESET_KIND_CAMERA_MOTION,
  VYBY_PRESET_KIND_CAMERA_TRANSITION_IN,
  VYBY_PRESET_KIND_CAMERA_TRANSITION_OUT,
} from '@/etc/cameraPresetKinds'

export const VYBY_PRESETS_TABLE = 'vyby_presets'

export const VYBY_PRESET_KIND_EFFECT = 'effect'
export const VYBY_PRESET_KIND_TEXT_MOTION = 'text_motion'
export const VYBY_PRESET_KIND_NODE_WORKFLOW = 'node_workflow'
export const VYBY_PRESET_KIND_PERSONALITY = 'personality'
export const VYBY_PRESET_KIND_MOOD = 'mood'

export const VYBY_PRESET_KINDS = [
  VYBY_PRESET_KIND_EFFECT,
  VYBY_PRESET_KIND_TEXT_MOTION,
  VYBY_PRESET_KIND_NODE_WORKFLOW,
  VYBY_PRESET_KIND_PERSONALITY,
  VYBY_PRESET_KIND_MOOD,
  VYBY_PRESET_KIND_CAMERA_BODY,
  VYBY_PRESET_KIND_CAMERA_LENS,
  VYBY_PRESET_KIND_CAMERA_FOCAL,
  VYBY_PRESET_KIND_CAMERA_DEPTH_OF_FIELD,
  VYBY_PRESET_KIND_CAMERA_TRANSITION_IN,
  VYBY_PRESET_KIND_CAMERA_MOTION,
  VYBY_PRESET_KIND_CAMERA_TRANSITION_OUT,
]

export const VYBY_PRESET_SOURCE_GLOBAL = 'global'

const KIND_ALIASES = {
  effect: VYBY_PRESET_KIND_EFFECT,
  effects: VYBY_PRESET_KIND_EFFECT,
  effect_preset: VYBY_PRESET_KIND_EFFECT,
  text: VYBY_PRESET_KIND_TEXT_MOTION,
  text_motion: VYBY_PRESET_KIND_TEXT_MOTION,
  text_animation: VYBY_PRESET_KIND_TEXT_MOTION,
  text_animations: VYBY_PRESET_KIND_TEXT_MOTION,
  workflow: VYBY_PRESET_KIND_NODE_WORKFLOW,
  workflows: VYBY_PRESET_KIND_NODE_WORKFLOW,
  node_workflow: VYBY_PRESET_KIND_NODE_WORKFLOW,
  node_workflows: VYBY_PRESET_KIND_NODE_WORKFLOW,
  personality: VYBY_PRESET_KIND_PERSONALITY,
  personalities: VYBY_PRESET_KIND_PERSONALITY,
  ai_personality: VYBY_PRESET_KIND_PERSONALITY,
  ai_personalities: VYBY_PRESET_KIND_PERSONALITY,
  mood: VYBY_PRESET_KIND_MOOD,
  moods: VYBY_PRESET_KIND_MOOD,
  scene: VYBY_PRESET_KIND_MOOD,
  scenes: VYBY_PRESET_KIND_MOOD,
  ai_mood: VYBY_PRESET_KIND_MOOD,
  ai_moods: VYBY_PRESET_KIND_MOOD,
  camera: VYBY_PRESET_KIND_CAMERA_BODY,
  cameras: VYBY_PRESET_KIND_CAMERA_BODY,
  camera_body: VYBY_PRESET_KIND_CAMERA_BODY,
  camera_bodies: VYBY_PRESET_KIND_CAMERA_BODY,
  lens: VYBY_PRESET_KIND_CAMERA_LENS,
  lenses: VYBY_PRESET_KIND_CAMERA_LENS,
  camera_lens: VYBY_PRESET_KIND_CAMERA_LENS,
  camera_lenses: VYBY_PRESET_KIND_CAMERA_LENS,
  focal: VYBY_PRESET_KIND_CAMERA_FOCAL,
  focal_style: VYBY_PRESET_KIND_CAMERA_FOCAL,
  focal_styles: VYBY_PRESET_KIND_CAMERA_FOCAL,
  camera_focal: VYBY_PRESET_KIND_CAMERA_FOCAL,
  depth_of_field: VYBY_PRESET_KIND_CAMERA_DEPTH_OF_FIELD,
  camera_depth_of_field: VYBY_PRESET_KIND_CAMERA_DEPTH_OF_FIELD,
  aperture: VYBY_PRESET_KIND_CAMERA_DEPTH_OF_FIELD,
  camera_transition_in: VYBY_PRESET_KIND_CAMERA_TRANSITION_IN,
  transition_in: VYBY_PRESET_KIND_CAMERA_TRANSITION_IN,
  camera_motion: VYBY_PRESET_KIND_CAMERA_MOTION,
  motion: VYBY_PRESET_KIND_CAMERA_MOTION,
  camera_transition_out: VYBY_PRESET_KIND_CAMERA_TRANSITION_OUT,
  transition_out: VYBY_PRESET_KIND_CAMERA_TRANSITION_OUT,
}

const cloneJson = (value) => {
  if (typeof value === 'undefined') return undefined
  try {
    return JSON.parse(JSON.stringify(value))
  } catch (e) {
    return undefined
  }
}

const compactObject = (value = {}) =>
  Object.entries(value || {}).reduce((acc, [key, next]) => {
    if (typeof next !== 'undefined') acc[key] = next
    return acc
  }, {})

const firstString = (...values) => {
  for (const value of values) {
    const next = `${value || ''}`.trim()
    if (next) return next
  }
  return ''
}

const safeGlobalElementId = (stableId) => {
  const safe = `${stableId || ''}`.trim().replace(/[^A-Za-z0-9_-]+/g, '-')
  return `global-personality-${safe || 'preset'}`
}

const safeGlobalMoodId = (stableId) => {
  const safe = `${stableId || ''}`.trim().replace(/[^A-Za-z0-9_-]+/g, '-')
  return `global-mood-${safe || 'preset'}`
}

// Public helpers so the CMS can predict the element id a promoted binding
// will resolve to (used by the Node Workflows promotion flow).
export const globalPersonalityElementId = (stableId) => safeGlobalElementId(stableId)
export const globalMoodElementId = (stableId) => safeGlobalMoodId(stableId)

export const normalizeVybyPresetKind = (kind) => {
  const raw = `${kind || ''}`.trim().toLowerCase()
  return KIND_ALIASES[raw] || ''
}

export const normalizeVybyPresetTags = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((tag) => `${tag || ''}`.trim())
      .filter(Boolean)
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean)
  }
  return []
}

const normalizeSortOrder = (value) => {
  const next = Number(value)
  return Number.isFinite(next) ? Math.floor(next) : null
}

const normalizePayloadObject = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null
  return cloneJson(payload) || null
}

export const normalizeVybyPresetRow = (row = {}) => {
  if (!row || typeof row !== 'object') return null
  const payload = normalizePayloadObject(row.payload)
  if (!payload) return null

  const kind = normalizeVybyPresetKind(row.kind || payload.kind)
  if (!kind) return null

  const stableId = firstString(
    row.stable_id,
    row.stableId,
    row.code,
    payload.stable_id,
    payload.stableId,
    payload.id,
    payload.code
  )
  const name = firstString(row.name, payload.name)
  if (!stableId || !name) return null

  return compactObject({
    id: firstString(row.id),
    kind,
    stableId,
    stable_id: stableId,
    name,
    description:
      typeof row.description === 'string'
        ? row.description
        : typeof payload.description === 'string'
          ? payload.description
          : '',
    intentTags: normalizeVybyPresetTags(row.intent_tags || row.intentTags || payload.intentTags),
    intent_tags: normalizeVybyPresetTags(row.intent_tags || row.intentTags || payload.intentTags),
    image: firstString(row.image, row.img, payload.image, payload.img),
    img: firstString(row.image, row.img, payload.image, payload.img),
    category: firstString(row.category, payload.category),
    group: firstString(row.group, payload.group),
    payload,
    enabled: row.enabled !== false,
    sortOrder: normalizeSortOrder(row.sort_order ?? row.sortOrder),
    sort_order: normalizeSortOrder(row.sort_order ?? row.sortOrder),
    createdAt: firstString(row.created_at, row.createdAt),
    created_at: firstString(row.created_at, row.createdAt),
    updatedAt: firstString(row.updated_at, row.updatedAt),
    updated_at: firstString(row.updated_at, row.updatedAt),
  })
}

export const normalizeVybyPresetRows = (rows = []) =>
  (Array.isArray(rows) ? rows : [])
    .map(normalizeVybyPresetRow)
    .filter(Boolean)
    .sort((a, b) => {
      const ao = a.sortOrder === null ? Number.POSITIVE_INFINITY : a.sortOrder
      const bo = b.sortOrder === null ? Number.POSITIVE_INFINITY : b.sortOrder
      if (ao !== bo) return ao - bo
      return `${a.name || ''}`.localeCompare(`${b.name || ''}`)
    })

export const groupVybyPresetRowsByKind = (rows = []) =>
  normalizeVybyPresetRows(rows).reduce((acc, row) => {
    if (!acc[row.kind]) acc[row.kind] = []
    acc[row.kind].push(row)
    return acc
  }, {})

export const cmsPresetRowForSave = (preset = {}) => {
  const row = normalizeVybyPresetRow({
    ...preset,
    payload: preset.payload || {},
  })
  if (!row) return null

  return {
    kind: row.kind,
    stable_id: row.stableId,
    name: row.name,
    description: row.description || '',
    intent_tags: row.intentTags || [],
    image: row.image || '',
    category: row.category || '',
    group: row.group || '',
    payload: row.payload || {},
    enabled: row.enabled !== false,
    sort_order: row.sortOrder,
  }
}

export const vybyPresetRowToEffectPreset = (row) => {
  const normalized = normalizeVybyPresetRow(row)
  if (!normalized || normalized.kind !== VYBY_PRESET_KIND_EFFECT) return null

  const source = normalized.payload || {}
  const presetPayload =
    source.preset && typeof source.preset === 'object'
      ? source.preset
      : source
  const group = firstString(normalized.group, source.group, source.preset?.group)
  if (!group || !presetPayload || typeof presetPayload !== 'object') return null

  return createEffectGlobalPresetExport({
    id: normalized.stableId,
    name: normalized.name,
    group,
    img: normalized.image,
    description: normalized.description,
    intentTags: normalized.intentTags,
    preset: presetPayload,
  })
}

export const vybyPresetRowsToEffectPresets = (rows = []) =>
  normalizeVybyPresetRows(rows)
    .map(vybyPresetRowToEffectPreset)
    .filter(Boolean)

export const vybyPresetRowToTextMotionPreset = (row) => {
  const normalized = normalizeVybyPresetRow(row)
  if (!normalized || normalized.kind !== VYBY_PRESET_KIND_TEXT_MOTION) return null

  const payload = normalized.payload || {}
  return normalizeTextMotionPreset(
    createTextMotionGlobalPresetExport({
      ...payload,
      id: normalized.stableId,
      name: normalized.name,
      description: normalized.description,
      intentTags: normalized.intentTags,
    })
  )
}

export const vybyPresetRowsToTextMotionPresets = (rows = []) =>
  normalizeVybyPresetRows(rows)
    .map(vybyPresetRowToTextMotionPreset)
    .filter(Boolean)

export const vybyPresetRowToNodeWorkflowPreset = (row) => {
  const normalized = normalizeVybyPresetRow(row)
  if (!normalized || normalized.kind !== VYBY_PRESET_KIND_NODE_WORKFLOW) return null

  const source = normalized.payload || {}
  const graph =
    source.graph && typeof source.graph === 'object'
      ? source.graph
      : source
  const preset = normalizeWorkflowPreset({
    ...source,
    id: normalized.stableId,
    name: normalized.name,
    description: normalized.description,
    category: normalized.category || source.category,
    createdAt: normalized.createdAt || source.createdAt,
    updatedAt: normalized.updatedAt || source.updatedAt,
    graph,
  })
  if (!preset) return null

  return {
    ...preset,
    source: VYBY_PRESET_SOURCE_GLOBAL,
    intentTags: normalized.intentTags,
    image: normalized.image,
    img: normalized.image,
  }
}

export const vybyPresetRowsToNodeWorkflowPresets = (rows = []) =>
  normalizeVybyPresetRows(rows)
    .map(vybyPresetRowToNodeWorkflowPreset)
    .filter(Boolean)

export const vybyPresetRowToPersonalityElement = (row) => {
  const normalized = normalizeVybyPresetRow(row)
  if (!normalized || normalized.kind !== VYBY_PRESET_KIND_PERSONALITY) return null

  const payload = normalized.payload || {}
  const prompt = firstString(payload.prompt, payload.instruction, payload.systemPrompt)
  if (!prompt) return null

  const stableId = normalized.stableId
  const categorySource =
    Array.isArray(payload.categories) && payload.categories.length
      ? payload.categories
      : payload.categories || normalized.category || payload.category
  const categories = normalizePersonalityCategories(categorySource)
  return {
    id: safeGlobalElementId(stableId),
    name: normalized.name,
    kind: 'personality',
    prompt,
    thumbnail: normalized.image || '',
    images: [],
    category: normalizePersonalityCategory(normalized.category || categories[0]),
    categories,
    source: VYBY_PRESET_SOURCE_GLOBAL,
    readOnly: true,
    global: true,
    presetId: normalized.id || '',
    stableId,
    stable_id: stableId,
    description: normalized.description || '',
    intentTags: normalized.intentTags || [],
  }
}

export const vybyPresetRowsToPersonalityElements = (rows = []) =>
  normalizeVybyPresetRows(rows)
    .map(vybyPresetRowToPersonalityElement)
    .filter(Boolean)

// Moods are text-only elements (no thumbnail/images) selected via the node
// canvas Scene chip. Global mood presets mirror global personalities: they
// merge into `user/aiElements` read-only so bindings in global workflow
// presets resolve for every user.
export const vybyPresetRowToMoodElement = (row) => {
  const normalized = normalizeVybyPresetRow(row)
  if (!normalized || normalized.kind !== VYBY_PRESET_KIND_MOOD) return null

  const payload = normalized.payload || {}
  const prompt = firstString(payload.prompt, payload.instruction, payload.systemPrompt)
  if (!prompt) return null

  const stableId = normalized.stableId
  return {
    id: safeGlobalMoodId(stableId),
    name: normalized.name,
    kind: 'mood',
    prompt,
    thumbnail: '',
    images: [],
    category: normalizeMoodCategory(normalized.category || payload.category),
    source: VYBY_PRESET_SOURCE_GLOBAL,
    readOnly: true,
    global: true,
    presetId: normalized.id || '',
    stableId,
    stable_id: stableId,
    description: normalized.description || '',
    intentTags: normalized.intentTags || [],
  }
}

export const vybyPresetRowsToMoodElements = (rows = []) =>
  normalizeVybyPresetRows(rows)
    .map(vybyPresetRowToMoodElement)
    .filter(Boolean)

export const vybyPresetRowToDomainPreset = (row) => {
  const normalized = normalizeVybyPresetRow(row)
  if (!normalized) return null
  if (normalized.kind === VYBY_PRESET_KIND_EFFECT) {
    return vybyPresetRowToEffectPreset(normalized)
  }
  if (normalized.kind === VYBY_PRESET_KIND_TEXT_MOTION) {
    return vybyPresetRowToTextMotionPreset(normalized)
  }
  if (normalized.kind === VYBY_PRESET_KIND_NODE_WORKFLOW) {
    return vybyPresetRowToNodeWorkflowPreset(normalized)
  }
  if (normalized.kind === VYBY_PRESET_KIND_PERSONALITY) {
    return vybyPresetRowToPersonalityElement(normalized)
  }
  if (normalized.kind === VYBY_PRESET_KIND_MOOD) {
    return vybyPresetRowToMoodElement(normalized)
  }
  return null
}

export const vybyPresetRowsToDomainPresets = (rows = []) =>
  normalizeVybyPresetRows(rows)
    .map(vybyPresetRowToDomainPreset)
    .filter(Boolean)

export const isVybyGlobalEffectPreset = (preset = {}) =>
  preset?.source === EFFECT_PRESET_SOURCE_GLOBAL

export const isVybyGlobalTextMotionPreset = (preset = {}) =>
  preset?.source === TEXT_MOTION_PRESET_SOURCE_GLOBAL
